/**
 * App-wide access to the platform health store. Wraps the platform-selected
 * `healthProvider` (HealthKit / Health Connect / no-op) and exposes a small,
 * UI-friendly surface: availability, a normalized permission status, and a
 * `connect()` action. The concrete data reads are stubs in this slice (#29/#36).
 */
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useContext } from '@/hooks/useContext';
import { logger } from '@/lib/logger';
import {
  healthProvider,
  HealthDataSource,
  HealthDateRange,
  HealthPermissionStatus,
  HealthScope,
} from '@/features/health/lib/health-provider/health-provider';

/** The card is the single integrations home, so it connects every scope at once. */
const ALL_SCOPES: HealthScope[] = [HealthScope.WORKOUT, HealthScope.BODY];

export interface HealthContext {
  /** Whether this platform has a health store at all (false on web). */
  isAvailable: boolean;
  source: HealthDataSource | null;
  permissionStatus: HealthPermissionStatus;
  isConnected: boolean;
  /** Some but not all requested data types shared (Health Connect). */
  isPartiallyConnected: boolean;
  isRequesting: boolean;
  /** Opens the OS permission sheet; resolves to the new status. */
  connect: () => Promise<HealthPermissionStatus>;
  /** Opens the OS health-management UI to change what's shared. */
  openSettings: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  readWorkoutSessions: HealthProviderReads['readWorkoutSessions'];
  readBodyMeasurements: HealthProviderReads['readBodyMeasurements'];
}

type HealthProviderReads = Pick<
  typeof healthProvider,
  'readWorkoutSessions' | 'readBodyMeasurements'
>;

const ctx = createContext<HealthContext | undefined>(undefined);
ctx.displayName = 'Health';

export interface HealthProviderProps {
  children?: ReactNode;
}

export const HealthContextProvider = ({ children }: HealthProviderProps) => {
  const isAvailable = healthProvider.isAvailable();
  const [permissionStatus, setPermissionStatus] =
    useState<HealthPermissionStatus>(
      isAvailable
        ? HealthPermissionStatus.UNDETERMINED
        : HealthPermissionStatus.UNAVAILABLE,
    );
  const [isRequesting, setIsRequesting] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!isAvailable) return;
    try {
      setPermissionStatus(await healthProvider.getPermissionStatus(ALL_SCOPES));
    } catch (error) {
      logger.error(error, '[Health] refreshStatus failed');
    }
  }, [isAvailable]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  const connect = useCallback(async () => {
    if (!isAvailable) return HealthPermissionStatus.UNAVAILABLE;
    setIsRequesting(true);
    try {
      const status = await healthProvider.requestPermissions(ALL_SCOPES);
      setPermissionStatus(status);
      return status;
    } catch (error) {
      logger.error(error, '[Health] connect failed');
      return HealthPermissionStatus.DENIED;
    } finally {
      setIsRequesting(false);
    }
  }, [isAvailable]);

  const openSettings = useCallback(() => healthProvider.openSettings(), []);

  const readWorkoutSessions = useCallback(
    (range: HealthDateRange) => healthProvider.readWorkoutSessions(range),
    [],
  );
  const readBodyMeasurements = useCallback(
    (range: HealthDateRange) => healthProvider.readBodyMeasurements(range),
    [],
  );

  const value = useMemo<HealthContext>(
    () => ({
      isAvailable,
      source: healthProvider.source,
      permissionStatus,
      isConnected: permissionStatus === HealthPermissionStatus.GRANTED,
      isPartiallyConnected: permissionStatus === HealthPermissionStatus.PARTIAL,
      isRequesting,
      connect,
      openSettings,
      refreshStatus,
      readWorkoutSessions,
      readBodyMeasurements,
    }),
    [
      isAvailable,
      permissionStatus,
      isRequesting,
      connect,
      openSettings,
      refreshStatus,
      readWorkoutSessions,
      readBodyMeasurements,
    ],
  );

  return <ctx.Provider value={value}>{children}</ctx.Provider>;
};

export const useHealth = () => useContext(ctx);
